import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, ValidatorFn, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { AccountService } from 'src/app/services/account.service';
import { ClaimService } from 'src/app/services/claim.service';
import { emptyIEmail, IEmail } from 'src/app/shared/models/email.interface';
import { EMAIL_PATTERN } from 'src/app/shared/regex-patterns';
import { TupssService } from '../../../services/tupss.service';
import { IVerificationInterface, emptyVerificationInterface } from 'src/app/shared/models/verification.interface';
import { environment } from 'src/environments/environment';
import { AuthService, IdToken } from '@auth0/auth0-angular';
import { LoginService } from 'src/app/services/login.service';
import { filter, take } from 'rxjs/operators';
import { ScriptService } from 'src/app/services/script.service';

@Component({
  selector: 'upsc-start',
  templateUrl: './start.component.html',
  styleUrls: ['./start.component.scss']
})
export class StartComponent implements OnInit {
  @Output() nextEvent = new EventEmitter<any>();
  
  recaptchaSiteKey = environment.recaptcha.siteKeyV3;
  recaptchaSecret = environment.recaptcha.secretV3;
  //Form
  customerForm: UntypedFormGroup;

  //Page display logic
  showEmailVerification: boolean;
  codeErrorReturned: boolean;
  currentlyCheckingDupeTrackingNumbers = false;
  systemError: boolean;
  loading: boolean = false;
  claimSubmittedAlready;
  appStateTarget = '';
  email = '';
  //Data models
  private emailModel: IEmail = emptyIEmail;
  verificationInterface = { emailId: "" };
  tupssType;
  errors: string[] = [];
  public createVerificationInterface: IVerificationInterface = emptyVerificationInterface;
  verifyCodeInterface = { emailId: "", toVerify: "" };
  iShipData;  //response from "checkShipment" API, contains basic address info, ship date, store location email+phone
  mustCompleteRecaptcha: boolean = false;

  //This component is only ever used for TCCP!

  constructor(
    private fb: UntypedFormBuilder,
    private router: Router,
    private accountService: AccountService,
    private claimService: ClaimService,
    private tupssService: TupssService,
    private auth: AuthService,
    private loginService: LoginService,
    private scriptService:ScriptService
  ) { 
    this.downloadCaptcha();
  }

  ngOnInit(): void {
    this.tupssType = this.router.url.includes('tfcp') ? 'tfcp' : 'tccp';
    sessionStorage.removeItem('policyDetails'); //reset the data for tccp. guarantees new contractID and policy search apis
    this.tupssService.userData = undefined; //reset the data for tccp. guarantees new contractID and policy search apis
    sessionStorage.setItem('userDetails', JSON.stringify({ type: this.tupssType, stage: 'pre' }));

    this.claimSubmittedAlready = this.tupssService._claimSubmittedFor ? true : false;
    if (this.claimSubmittedAlready) {
      this.tupssService.claimSubmittedFor = null;
    }

    this.setupForm();

    if (this.tupssType == 'tccp') {
      this.gatherAppState(); //Auth0
      this.determineLoginState();
    }

    this.customerForm.controls.shipmentId.valueChanges.subscribe(
      data => {
        this.customerForm.controls.transactionId.setErrors(null);
        this.customerForm.controls.shipmentId.setErrors(null);
      }
    );

    this.customerForm.controls.transactionId.valueChanges.subscribe(
      data => {
        this.customerForm.controls.transactionId.setErrors(null);
        this.customerForm.controls.shipmentId.setErrors(null);
      }
    );

  }

    //Will call recaptcha API and return a score for how likely a user is a bot.
  //closer to 0 is more likely a bot. closer to 1 is more likely a human
  reCAPTCHA(e?) {
    this.loading = true;
    //If user must complete a manual recaptcha, skip the API call.
    if (this.mustCompleteRecaptcha) {
      this.nextStep();
      return;
    }
    grecaptcha.ready(() => {
      grecaptcha.execute(this.recaptchaSiteKey, { action: 'submit' }).then((token) => {
        let request = {
          secret: this.recaptchaSecret,
          response: token,
          remoteip: ''
        }
        this.loginService.recaptchaCheck(request).subscribe(
          data => {
            if (data && data.score <= 0.2) {
              //show manual recaptcha
              this.loading = false;
              this.showReCAPTCHA();
            } else {
              //let user proceed
              this.nextStep();
            }
          }, error => {
            this.showReCAPTCHA();
          }
        );
      });
    });
  }
  downloadCaptcha() {
    this.scriptService.downloadCaptcha();
  }
  showReCAPTCHA() {
    this.mustCompleteRecaptcha = true;
    this.customerForm.controls.recaptchaControl.setValidators(Validators.required);
  }

  //Gathers the app state from Auth0, which may have been set by another component when they called loginWithRedirect. We use this app state to determine where to send the user after a successful login. If there is no app state, we will send them to the redirect URL or dashboard later
  //Used in CCP/SSP/passwordless flows
  gatherAppState() {
    this.auth.appState$.subscribe(
      appState => {
        if (appState && appState?.target) {
          console.log("User was authenticated, navigating to appState target: " + appState.target);
          this.appStateTarget = appState.target;
          this.router.navigateByUrl(appState.target);
        }
      }
    );
  }

  determineLoginState() {
    this.auth.idTokenClaims$.pipe(take(1)).subscribe(
      (response: IdToken | null | undefined) => {
        // if (response && response.sub && response.sub.startsWith('email|')) {
        //   console.log("if sub starts with email, they authenticated through the passwordless flow. Should go to a passwordless flow... what was appstate?");
        //   this.gatherAppState();
        //   return;
        // }
        if (!response) {
          this.initiatePasswordlessAuth();
        }
        if (response != null && response.__raw && response != undefined) {
          sessionStorage.setItem('data', JSON.stringify(response));
          sessionStorage.setItem('access_token', response.__raw);
          //sessionStorage.setItem('tccp-email', JSON.stringify({ email: response.email }));
          // sessionStorage.setItem('verifyData', JSON.stringify({ ...response, emailAddress: response.email }));
          //this.email = response.email;
          let request = {
            id_token: response.__raw,
            workflow: this.tupssType 
          }
          this.loginService.passwordLess(request).subscribe(
            data => {
              if (!data) {  //Make sure not to do anything with a processed token if we already have an app State. app state should have already redirected
                return;
              }
            }, error => {
              this.loading = false;
              this.systemError = true;
            }
          );
          //this.getPartnerPolicy();
        }
      });
  }

  initiatePasswordlessAuth() {
    console.log("initiating");
    this.auth.loginWithRedirect({
      authorizationParams: {
        connection: 'email',
        redirect_uri: `${environment.baseUrl.web}callback`,
        organization: environment.auth0.tupssOrganization,
        'ext-connect': 'email', //additional param to indicate template to hide "sign up" and "login" text
        'ext-style': 'tupss',
        // 'ext-type': 'tupss'
        //redirect_uri: `${'http://localhost:4200/'}callback`
      },
      appState: { target: `/${this.tupssType }` }
    });
  }

  setupForm() {
    this.customerForm = this.fb.group({
      trackingNumber: ['', Validators.compose([Validators.required, Validators.pattern(/^[a-z\-0-9]+$/i)])],
      //email: ['', Validators.compose([Validators.pattern(EMAIL_PATTERN)])],
      shipmentId: ['',],
      transactionId: ['',],
      recaptchaControl: [null]
    }, { validator: this.atLeastOneRequired });

    if (this.tupssType == 'tccp') {
      //this.email.setValidators([Validators.required, Validators.pattern(EMAIL_PATTERN)]);
    } else {
      //this.email.setValidators([Validators.pattern(EMAIL_PATTERN)]);
    }
  }

  //Check for available tracking number information
  getShipmentDetails() {
    this.systemError = false;
    //Don't call any API's if the field is not valid
    if (!this.customerForm.get('trackingNumber').valid) {
      return;
    }
    let trackingNumber = this.customerForm.get('trackingNumber').value.toUpperCase();
    this.currentlyCheckingDupeTrackingNumbers = true;

    //Check if the tracking number has been used to file a claim previously
    this.claimService.checkDuplicateClaims(trackingNumber).subscribe(
      data => {
        this.currentlyCheckingDupeTrackingNumbers = false;
        if (data.isDuplicateTrackingNumber) {
          this.customerForm.get('trackingNumber').setErrors({ 'inuse': true });
          this.errors.push('claims.edit.tupssErrors.existingClaim');
        } else {
          this.errors = this.errors.filter(x => x !== 'claims.edit.tupssErrors.existingClaim'); //removes existing duplicate error if any
          if (this.customerForm.get('trackingNumber').hasError('pattern')) {
            this.customerForm.get('trackingNumber').setErrors(null);
            this.customerForm.get('trackingNumber').setErrors({ 'pattern': true });
          } else if (this.customerForm.get('trackingNumber').hasError('required')) {
            this.customerForm.get('trackingNumber').setErrors(null);
            this.customerForm.get('trackingNumber').setErrors({ 'required': true });
          } else {
            this.customerForm.get('trackingNumber').setErrors(null);
          }
        }
      }, error => {
        this.currentlyCheckingDupeTrackingNumbers = false;
        this.systemError = true;
      }
    );
  }

  //User entered in the tracking number and shipment/transaction ID and clicked "Get Started"
  workFlow = '';
  nextStep() {
    this.loading = true;
    this.systemError = false;
    this.errors = [];
    const data = JSON.parse(sessionStorage.getItem('data'));
    if(data) {
      this.email = data.email;
    }
    if (this.trackingNumber.value.toUpperCase().startsWith('1Z') && this.trackingNumber.value.length != 18) {
      this.errors.push('claims.edit.tupssErrors.invalidTrackingNo');
      this.loading = false;
      return;
    }

    if (!this.email) {
      this.errors.push('claims.edit.tupssErrors.invalidEmail');
      this.loading = false;
      return;
    }
     //added this error since it's in the flow, but seemingly impossible to ever reach due to FormControl

    //First, check shipment to see if tracking number and shipmentID/transactionID match up.

    if (this.customerForm.valid) {
      let tupssRelatedInfo: any = {
        trackingNumber: this.trackingNumber.value.toUpperCase(),
        email: this.email,
        shipmentID: this.shipmentID.value,
        transactionID: this.transactionID.value,
        type: this.tupssType
      }
      let payload = {
        transactionSrc: 'Capital_TCCP',
        trackNo: this.trackingNumber.value.toUpperCase(),
        emailAddress: this.email,
        posReceiptID: this.transactionID.value,
        shipmentId: this.shipmentID.value
      }
      tupssRelatedInfo = {
        ...tupssRelatedInfo,
        type: payload.transactionSrc == 'Capital_TCCP' ? 'tccp' : 'tfcp',
        stage: 'pre'
      }
      this.tupssService.userDetails = tupssRelatedInfo;
      if (this.shipmentID?.value && this.transactionID?.value) {
        this.workFlow = 'tccp/' + this.trackingNumber?.value?.toUpperCase() + '/' + this.shipmentID.value + '/' + this.transactionID.value;
      } else if (this.shipmentID?.value) {
        this.workFlow = 'tccp/' + this.trackingNumber?.value?.toUpperCase() + '/' + this.shipmentID.value + '/';
      } else if (this.transactionID?.value) {
        this.workFlow = 'tccp/' + this.trackingNumber?.value?.toUpperCase() + '//' + this.transactionID.value;
      }
      this.tupssService.getIShipData(payload).subscribe(
        data => {
          this.loading = false;
          if (data && data.message == 'Success') {
            if (!data.data.storeNumber || !data.data.companyName) {
              this.systemError = true;
            } else if (data && data.data && data.data.response?.cdsresponse?.shipmentInfo?.billingInfo?.billingType == 3) {
              this.errors.push('claims.edit.tupssErrors.billThirdPartyTCCP');
            } else {
              this.systemError = false;
              this.iShipData = data.data;
              this.getPolicyInfo();
              //Do verification step here instead
              //this.saveVerificationCode();
            }
          }
          else if (data && data.message == 'NotFound' && !this.trackingNumber.value.toUpperCase().startsWith('1Z')) {
            this.errors.push('claims.edit.tupssErrors.notEligible');
          }
          else if (data && data.message == 'NotFound') {
            this.errors.push('claims.edit.tupssErrors.notCreatedFromTupss');
          }
          else if (data && data.message == 'ValidationError') { //Currently checkShipment returns 400 bad request error, so this is unreachable until API gets fixed to display 200 OK w/"ValidationError"
            this.errors.push('claims.edit.tupssErrors.oneIDRequired');
            this.customerForm.get('shipmentId').setErrors({ 'pattern': true });
            this.customerForm.get('transactionId').setErrors({ 'pattern': true });
          }
          else if (data && data.message == 'BillRecipient') {
            this.errors.push('claims.edit.tupssErrors.billRecipientTCCP');
          }
          else if (data && data.message == 'BMAUPS') {
            this.errors.push('claims.edit.tupssErrors.billMyAccountTCCP');
          }
          else if (data && data.message == 'DateExceeded') {
            this.errors.push('claims.edit.tupssErrors.periodPassedTCCP');
          }
          else {
            this.errors.push('common.systemError');
          }
        }, error => {
          this.loading = false;
          this.systemError = true;
        }
      );
    }
  }

  // saveVerificationCode() {
  //   this.createVerificationInterface.data.emailAddress = this.email.value;
  //   this.accountService.createVerificationCode(this.createVerificationInterface).subscribe(
  //     data => {
  //       if((data.responseMessage).toLowerCase() === 'new verification code created' || (data.responseMessage).toLowerCase() === 'code not yet expired') {
  //         this.showEmailVerification = true;
  //       }
  //     }, error => {
  //     this.loading = false;
  //     this.systemError = true;
  //     this.showEmailVerification = false;
  //     }
  //   );
  // }

  getPolicyInfo() {
    this.tupssService.getContractId(this.iShipData.storeNumber, '', this.iShipData.shipDate).subscribe(
      res => {
        if (res.data && res.data.contractIDResponse) {
          //This tracking number is returning with more than 1 policy.
          //If TN was 1Z, use UPSSTORE programtype.
          //If TN was NOT 1Z, use FLEX programtype.
          let applicableContract = this.trackingNumber.value.toUpperCase().startsWith('1Z') ? res.data.contractIDResponse.find(cd => cd && cd.programType == 'UPSSTORES') : res.data.contractIDResponse.find(cd => cd && cd.programType == 'FLEX');
          if (applicableContract) {
            //hardcoded for UAT testing - its fine to remove this later
            /* if (environment.baseUrl.urlType == 'uat') {
              applicableContract.contractID = '15914024-241843';
            } */
            this.tupssService.setContractId(applicableContract.contractID);
            this.router.navigate(['tupss', 'file']);
            this.claimService.setStep();
          } else {
            this.errors.push('claims.edit.tupssErrors.notEligible');
            this.loading = false;
          }
        } else {
          this.showEmailVerification = false;
          this.systemError = true;
          this.loading = false;
        }
      }, error => {
        this.showEmailVerification = false;
        this.systemError = true;
        this.loading = false;
      }
    )
  }

  // cancel() {
  //   this.showEmailVerification = false;
  // }

  closeErrorMsg() {
    this.errors = [];
  }

  openFAQs(event) {
    event.preventDefault();
    this.tupssService.openFaqsQ14();
  }

  processLinks(event) {
    const element: HTMLElement = event.target;
    if (this.errors.includes('claims.edit.tupssErrors.existingClaim')) {
      if (element.nodeName === 'A') {
        event.preventDefault();
        this.tupssService.openFaqs();
      }
    }
  }

  atLeastOneRequired(group: UntypedFormGroup): { [s: string]: boolean } {
    if (group) {
      if (group.controls['shipmentId'].value || group.controls['transactionId'].value) {
        return null;
      }
    }
    return { 'error': true };
  }

  //get email() { return this.customerForm.get('email'); }
  get trackingNumber() { return this.customerForm.get('trackingNumber'); }
  get shipmentID() { return this.customerForm.get('shipmentId'); }
  get transactionID() { return this.customerForm.get('transactionId'); }
}
